import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getLocale, getString } from '../../../locale'
import {
    CatalogLocalImage,
    CatalogRemoteImage,
    CatalogStorageInfoResponse,
    deleteCatalogImage,
    downloadCatalogImage,
    getCatalogLocalList,
    getCatalogRemoteList,
    getCatalogStorageInfo,
} from '../../../api/0x0005/catalog'
import { listLtsp101CatalogImages, Ltsp101CatalogImage } from '../../../api/ltsp101-catalog'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    includeDownloaded: boolean
    remoteImages: CatalogRemoteImage[]
    localImages: CatalogLocalImage[]
    catalogImageTitlesHe: Record<number, string>
    storage?: CatalogStorageInfoResponse
}

export default class CatalogTab extends Component<Props, State> {
    state: State = {
        loading: true,
        includeDownloaded: false,
        remoteImages: [],
        localImages: [],
        catalogImageTitlesHe: {},
    }

    componentDidMount(): void {
        this.loadAll()
    }

    loadAll = async () => {
        try {
            this.setState({ loading: true })
            const [remote, local, storage] = await Promise.all([
                getCatalogRemoteList(this.props.clientId, this.state.includeDownloaded ? 1 : 0),
                getCatalogLocalList(this.props.clientId),
                getCatalogStorageInfo(this.props.clientId),
            ])
            const catalogImagesResult = await listLtsp101CatalogImages()
            const catalogImageTitlesHe = (catalogImagesResult.data || []).reduce<Record<number, string>>((acc, image: Ltsp101CatalogImage) => {
                if (image.titleHe) acc[image.id] = image.titleHe
                return acc
            }, {})

            this.setState({
                remoteImages: remote.data.images || [],
                localImages: local.data.images || [],
                catalogImageTitlesHe,
                storage: storage.data,
            })
        } catch (error) {
            toast.error((error as Error).message)
        } finally {
            this.setState({ loading: false })
        }
    }

    handleDownload = async (image: CatalogRemoteImage) => {
        try {
            const response = await downloadCatalogImage(this.props.clientId, image.id, image.title)
            if (response.data.success === 1) {
                toast.success(getString('ltsp101_catalog.image_download_success'))
                await this.loadAll()
            } else {
                toast.error(response.data.errorMsg || getString('ltsp101_catalog.image_download_error'))
            }
        } catch (error) {
            toast.error((error as Error).message)
        }
    }

    handleDelete = async (image: CatalogLocalImage) => {
        try {
            const response = await deleteCatalogImage(this.props.clientId, image.id)
            if (response.data.success === 1) {
                toast.success(getString('ltsp101_catalog.image_delete_success'))
                await this.loadAll()
            } else {
                toast.error(response.data.errorMsg || getString('ltsp101_catalog.image_delete_error'))
            }
        } catch (error) {
            toast.error((error as Error).message)
        }
    }

    render() {
        if (this.state.loading) {
            return <div className="text-center p-8">{getString('ltsp101_catalog.loading')}</div>
        }

        const { storage } = this.state
        const isHebrew = getLocale() === 'he'
        return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold dark:text-white">{getString('ltsp101_catalog.catalog')}</h2>
                    <div className="flex items-center gap-4">
                        <label className="text-sm dark:text-gray-200 flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={this.state.includeDownloaded}
                                onChange={async e => {
                                    this.setState({ includeDownloaded: e.target.checked }, this.loadAll)
                                }}
                            />
                            {getString('ltsp101_catalog.include_downloaded')}
                        </label>
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded" onClick={this.loadAll}>
                            {getString('ltsp101_catalog.refresh')}
                        </button>
                    </div>
                </div>

                {storage && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="p-3 rounded border dark:border-gray-700">
                            <div className="text-gray-500">{getString('ltsp101_catalog.total')}</div>
                            <div className="font-semibold dark:text-white">{Math.round(storage.totalBytes / 1024 / 1024)} MB</div>
                        </div>
                        <div className="p-3 rounded border dark:border-gray-700">
                            <div className="text-gray-500">{getString('ltsp101_catalog.used')}</div>
                            <div className="font-semibold dark:text-white">{Math.round(storage.usedBytes / 1024 / 1024)} MB</div>
                        </div>
                        <div className="p-3 rounded border dark:border-gray-700">
                            <div className="text-gray-500">{getString('ltsp101_catalog.free')}</div>
                            <div className="font-semibold dark:text-white">{Math.round(storage.freeBytes / 1024 / 1024)} MB</div>
                        </div>
                    </div>
                )}

                <div>
                    <h3 className="text-lg font-semibold mb-3 dark:text-white">{getString('ltsp101_catalog.remote_images')}</h3>
                    <div className="space-y-2">
                        {this.state.remoteImages.map(image => (
                            <div key={`remote-${image.id}`} className="flex items-center justify-between border dark:border-gray-700 rounded p-3">
                                <div className="dark:text-white">
                                    #{image.id} - {isHebrew && this.state.catalogImageTitlesHe[image.id] ? this.state.catalogImageTitlesHe[image.id] : image.title}
                                </div>
                                <button
                                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded"
                                    onClick={() => this.handleDownload(image)}
                                >
                                    {getString('ltsp101_catalog.download')}
                                </button>
                            </div>
                        ))}
                        {this.state.remoteImages.length === 0 && <div className="text-sm text-gray-500">{getString('ltsp101_catalog.no_remote_images')}</div>}
                    </div>
                </div>

                <div>
                    <h3 className="text-lg font-semibold mb-3 dark:text-white">{getString('ltsp101_catalog.local_images')}</h3>
                    <div className="space-y-2">
                        {this.state.localImages.map(image => (
                            <div key={`local-${image.id}`} className="flex items-center justify-between border dark:border-gray-700 rounded p-3">
                                <div className="dark:text-white">
                                    #{image.id} - {isHebrew && this.state.catalogImageTitlesHe[image.id] ? this.state.catalogImageTitlesHe[image.id] : image.title} ({image.width}x{image.height}, {image.bpp}bpp)
                                </div>
                                <button
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                                    onClick={() => this.handleDelete(image)}
                                >
                                    {getString('ltsp101_catalog.delete')}
                                </button>
                            </div>
                        ))}
                        {this.state.localImages.length === 0 && <div className="text-sm text-gray-500">{getString('ltsp101_catalog.no_local_images')}</div>}
                    </div>
                </div>
            </div>
        )
    }
}
